/**
 * AnimationSystem — Global UI animation utilities.
 *
 * Injects CSS keyframes and provides helpers for smooth transitions.
 * Use this instead of abrupt style changes to make the UI feel polished.
 *
 * Patterns:
 * - fadeIn/fadeOut (opacity)
 * - slideIn/slideOut (transform)
 * - scaleIn/scaleOut (pop effect)
 * - pulse (attention)
 * - shake (error)
 * - ripple (click feedback)
 * - glow (hover emphasis)
 */

export class AnimationSystem {
  constructor() {
    this._injectCSS();
  }

  _injectCSS() {
    if (document.getElementById('fedeworld-animations')) return;
    const style = document.createElement('style');
    style.id = 'fedeworld-animations';
    style.textContent = `
      /* ========== KEYFRAMES ========== */
      @keyframes fw-fade-in  { from { opacity: 0; } to { opacity: 1; } }
      @keyframes fw-fade-out { from { opacity: 1; } to { opacity: 0; } }

      @keyframes fw-slide-down  {
        from { transform: translateY(-30px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes fw-slide-up  {
        from { transform: translateY(30px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes fw-slide-left  {
        from { transform: translateX(30px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes fw-slide-right  {
        from { transform: translateX(-30px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }

      @keyframes fw-scale-in {
        from { transform: scale(0.85); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      @keyframes fw-scale-out {
        from { transform: scale(1); opacity: 1; }
        to { transform: scale(0.85); opacity: 0; }
      }
      @keyframes fw-pop-in {
        0% { transform: scale(0.7); opacity: 0; }
        60% { transform: scale(1.08); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }

      @keyframes fw-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes fw-pulse-strong {
        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.7); }
        50% { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(255, 215, 0, 0); }
      }

      @keyframes fw-shake {
        0%, 100% { transform: translateX(0); }
        20% { transform: translateX(-4px); }
        40% { transform: translateX(4px); }
        60% { transform: translateX(-3px); }
        80% { transform: translateX(3px); }
      }

      @keyframes fw-glow {
        0%, 100% { box-shadow: 0 0 5px rgba(255, 215, 0, 0.3); }
        50% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.8); }
      }

      @keyframes fw-ripple {
        to { transform: scale(2); opacity: 0; }
      }

      @keyframes fw-rotate-360 {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes fw-bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }

      /* ========== UTILITY CLASSES ========== */
      .fw-anim-fade-in    { animation: fw-fade-in 0.25s ease-out; }
      .fw-anim-fade-out   { animation: fw-fade-out 0.2s ease-in forwards; }
      .fw-anim-slide-down { animation: fw-slide-down 0.3s cubic-bezier(.34,1.56,.64,1); }
      .fw-anim-slide-up   { animation: fw-slide-up 0.3s cubic-bezier(.34,1.56,.64,1); }
      .fw-anim-slide-left { animation: fw-slide-left 0.3s cubic-bezier(.34,1.56,.64,1); }
      .fw-anim-slide-right{ animation: fw-slide-right 0.3s cubic-bezier(.34,1.56,.64,1); }
      .fw-anim-scale-in   { animation: fw-scale-in 0.25s cubic-bezier(.34,1.56,.64,1); }
      .fw-anim-pop-in     { animation: fw-pop-in 0.35s cubic-bezier(.34,1.56,.64,1); }
      .fw-anim-pulse      { animation: fw-pulse 1.5s ease-in-out infinite; }
      .fw-anim-pulse-strong { animation: fw-pulse-strong 1.2s ease-in-out infinite; }
      .fw-anim-shake      { animation: fw-shake 0.4s ease-in-out; }
      .fw-anim-glow       { animation: fw-glow 2s ease-in-out infinite; }
      .fw-anim-bounce     { animation: fw-bounce 0.6s ease-in-out; }

      /* ========== HOVER EFFECTS ========== */
      .fw-hover-lift {
        transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
      }
      .fw-hover-lift:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      }

      .fw-hover-scale {
        transition: transform 0.15s cubic-bezier(.34,1.56,.64,1);
      }
      .fw-hover-scale:hover {
        transform: scale(1.05);
      }

      .fw-hover-glow {
        transition: box-shadow 0.2s ease-out, border-color 0.2s ease-out;
      }
      .fw-hover-glow:hover {
        box-shadow: 0 0 15px rgba(255, 215, 0, 0.6);
      }

      .fw-hover-bright {
        transition: filter 0.15s ease-out, transform 0.15s ease-out;
      }
      .fw-hover-bright:hover {
        filter: brightness(1.2);
        transform: scale(1.03);
      }

      /* ========== RIPPLE ========== */
      .fw-ripple-container {
        position: relative;
        overflow: hidden;
      }
      .fw-ripple-dot {
        position: absolute;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.6);
        pointer-events: none;
        animation: fw-ripple 0.6s ease-out forwards;
      }

      /* ========== SMOOTH TRANSITIONS for common properties ========== */
      .fw-smooth {
        transition: all 0.2s ease-out;
      }

      /* ========== LOADING SPINNER ========== */
      .fw-spinner {
        display: inline-block;
        width: 24px;
        height: 24px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-top-color: #FFD700;
        border-radius: 50%;
        animation: fw-rotate-360 0.8s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Add a ripple effect on click to any element.
   * @param {HTMLElement} element
   */
  enableRipple(element) {
    element.classList.add('fw-ripple-container');
    element.addEventListener('click', (e) => {
      const rect = element.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 0.8;
      const dot = document.createElement('span');
      dot.className = 'fw-ripple-dot';
      dot.style.width = dot.style.height = size + 'px';
      dot.style.left = (e.clientX - rect.left - size / 2) + 'px';
      dot.style.top = (e.clientY - rect.top - size / 2) + 'px';
      element.appendChild(dot);
      setTimeout(() => dot.remove(), 600);
    });
  }

  /**
   * Apply smooth open animation to a panel.
   * @param {HTMLElement} el
   * @param {string} type - 'scale' | 'slide-down' | 'slide-up' | 'fade'
   */
  animateOpen(el, type = 'scale') {
    el.classList.remove('fw-anim-fade-out', 'fw-anim-scale-out');
    const map = {
      'scale': 'fw-anim-scale-in',
      'pop': 'fw-anim-pop-in',
      'slide-down': 'fw-anim-slide-down',
      'slide-up': 'fw-anim-slide-up',
      'slide-left': 'fw-anim-slide-left',
      'slide-right': 'fw-anim-slide-right',
      'fade': 'fw-anim-fade-in',
    };
    el.classList.add(map[type] || 'fw-anim-fade-in');
    // Remove class after animation to allow re-trigger
    setTimeout(() => el.classList.remove(map[type] || 'fw-anim-fade-in'), 400);
  }

  /**
   * Apply close animation and call callback when done.
   */
  animateClose(el, callback, type = 'scale') {
    const map = {
      'scale': 'fw-anim-scale-out',
      'fade': 'fw-anim-fade-out',
    };
    el.classList.add(map[type] || 'fw-anim-fade-out');
    setTimeout(() => {
      el.classList.remove(map[type] || 'fw-anim-fade-out');
      if (callback) callback();
    }, 200);
  }

  /** Shake element (error feedback) */
  shake(el) {
    el.classList.add('fw-anim-shake');
    setTimeout(() => el.classList.remove('fw-anim-shake'), 400);
  }

  /** Pulse element briefly (attention) */
  pulse(el) {
    el.classList.add('fw-anim-pulse-strong');
    setTimeout(() => el.classList.remove('fw-anim-pulse-strong'), 2400);
  }

  /** Add hover lift effect to element */
  makeInteractive(el, hoverType = 'lift') {
    const map = { lift: 'fw-hover-lift', scale: 'fw-hover-scale', glow: 'fw-hover-glow', bright: 'fw-hover-bright' };
    el.classList.add(map[hoverType] || 'fw-hover-lift');
    this.enableRipple(el);
  }
}

// Singleton instance (import and use anywhere)
export const animations = new AnimationSystem();
